import { Info } from 'lucide-react'
import { useMemo } from 'react'
import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { CustomerFlowSection } from '@/components/px/CustomerFlowSection'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { pxNav } from '@/components/px/nav'
import {
  ARCHITECTURE_CHROME,
  ARCHITECTURE_DENSITY_NOTE,
  ARCHITECTURE_HEADER,
  CROSSWALK,
  FLOW_INTRO,
  INVENTORY,
  LOOP_LABELS,
  PHASES_TABLE,
  RUN_AND_EVOLVE_LABELS,
  SECTION_HEADINGS,
  crosswalkRows,
  levelCards,
  reverseCrosswalkRows,
} from '@/demo/content/architecture'
import {
  ENGAGEMENT_PHASES,
  EVIDENCE_EDGES,
  LOOP_NOTE,
  MAX_LEVERAGE,
  MODULE_FLOW,
  MODULE_FLOW_CAPTION,
  REMOVED_MODULES,
  RUN_AND_EVOLVE,
  SHEET_2_FRAMING,
  SHEET_2_RISK,
} from '@/modules/architecture'
import { FLOW_ACTIVATION, FLOW_EDGES, FLOW_LANES, FLOW_STEPS } from '@/demo/customerFlow'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'

/**
 * `/architecture` — the Rev A map. Six sections in the order a reviewer
 * holding the PDF reads them: the loop, the seven gated phases, the customer
 * flow, the crosswalk between the two lifecycles, the L0–L4 inventory, and
 * sheet 3's Run & evolve block marked out of scope.
 *
 * ⚠ ConceptBadged (AC-7.1): the surface asserts a taxonomy the product intends
 * rather than one the runtime has.
 *
 * ⚠ The inventory renders the Rev A FOOTER verbatim (45 · 20 · 25 · 7) and,
 * beside it, the reconciliation note stating the enumerated 44. The delta is
 * shown, not silently closed: no row was invented and none was dropped.
 */
export default function ArchitectureMapPage() {
  const role = useAppSelector(selectActiveRoleKey)
  const crosswalk = useMemo(() => crosswalkRows(), [])
  const reverse = useMemo(() => reverseCrosswalkRows(), [])
  const levels = useMemo(() => levelCards(), [])

  return (
    <AppShell
      moduleLabel={ARCHITECTURE_CHROME.moduleLabel}
      breadcrumb={ARCHITECTURE_CHROME.breadcrumb}
      contextPill={ARCHITECTURE_HEADER.contextPill}
      nav={pxNav(role)}
    >
      <div className="w-full">
        <div className="mb-3">
          <ConceptBadge />
        </div>
        <header className="max-w-3xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">
            {ARCHITECTURE_HEADER.title}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {ARCHITECTURE_HEADER.ledeBefore}
            <span className="font-medium text-foreground">{ARCHITECTURE_HEADER.source}</span>
            {ARCHITECTURE_HEADER.ledeMiddle}
            <span className="font-medium text-foreground">{ARCHITECTURE_HEADER.flowSource}</span>
            {ARCHITECTURE_HEADER.ledeAfter}
          </p>
        </header>

        {/* 1 · the three modules and the loop */}
        <h2 className="mt-8 font-serif text-xl font-semibold text-foreground">
          {SECTION_HEADINGS.loop}
        </h2>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">{MODULE_FLOW_CAPTION}</p>
        <div className="mt-4 grid grid-cols-3 items-start gap-4">
          {MODULE_FLOW.map((node) => (
            <section key={node.key} className="rounded-lg border border-border bg-card px-4 py-4">
              <h3 className="font-mono text-[0.75rem] font-semibold tracking-[0.08em] text-foreground">
                {node.title}
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">{node.subtitle}</p>
              <p className="mt-2 text-sm text-ink-soft">{node.caption}</p>
              {node.groups.map((group) => (
                <div key={group.heading} className="mt-3">
                  <SectionLabel>{group.heading}</SectionLabel>
                  <ul className="mt-1.5 flex flex-wrap gap-1">
                    {group.items.map((item) => (
                      <li
                        key={item}
                        className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[0.6875rem] text-ink-soft"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <SectionLabel>{LOOP_LABELS.evidence}</SectionLabel>
          {EVIDENCE_EDGES.map((edge) => (
            <div
              key={`${edge.from}-${edge.to}`}
              className="flex items-center gap-2 rounded-md border border-border bg-accent px-3 py-2 text-xs text-accent-foreground"
            >
              <span className="font-semibold">{edge.from}</span>
              <span aria-hidden>→</span>
              <span className="font-semibold">{edge.to}</span>
              <span className="text-ink-soft">{edge.label}</span>
            </div>
          ))}
        </div>
        <p className="mt-2 max-w-4xl text-xs text-muted-foreground">{LOOP_NOTE}</p>

        {/* 2 · seven phases, seven gates */}
        <h2 className="mt-8 font-serif text-xl font-semibold text-foreground">
          {SECTION_HEADINGS.phases}
        </h2>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">{SHEET_2_FRAMING}</p>
        <div className="mt-4 rounded-lg border border-border bg-card px-4 py-2">
          <table className="w-full text-left">
            <thead>
              <tr>
                {PHASES_TABLE.columns.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="pb-2 pr-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ENGAGEMENT_PHASES.map((phase) => (
                <tr key={phase.key} className="border-t border-hairline align-top">
                  <td className="py-2.5 pr-3">
                    <span className="v-figure font-mono text-xs tabular-nums text-muted-foreground">
                      {String(phase.number).padStart(2, '0')}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="text-sm font-semibold text-foreground">{phase.name}</div>
                    <div className="text-xs text-muted-foreground">{phase.purpose}</div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className="font-mono text-[0.75rem] font-semibold tracking-[0.06em] text-foreground">
                      {phase.gateName}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-sm text-ink-soft">{phase.gateCriteria}</td>
                  <td className="py-2.5 pr-3">
                    <Leverage value={phase.leverage} />
                  </td>
                  <td className="py-2.5 pr-1 text-xs text-muted-foreground">
                    {phase.deliveryPhases.length === 0
                      ? PHASES_TABLE.preProject
                      : crosswalk
                          .filter((row) => phase.deliveryPhases.includes(row.deliveryKey))
                          .map((row) => row.deliveryName)
                          .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 flex items-start gap-2 rounded-md border border-review-bg bg-review-bg px-3 py-2 text-xs text-review">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{SHEET_2_RISK}</span>
        </p>

        {/* 3 · the customer flow */}
        <h2 className="mt-8 font-serif text-xl font-semibold text-foreground">
          {SECTION_HEADINGS.flow}
        </h2>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">{FLOW_INTRO}</p>
        <div className="mt-4">
          <CustomerFlowSection
            steps={FLOW_STEPS}
            activation={FLOW_ACTIVATION}
            edges={FLOW_EDGES}
            lanes={FLOW_LANES}
          />
        </div>

        {/* 4 · two lifecycles, one crosswalk */}
        <h2 className="mt-8 font-serif text-xl font-semibold text-foreground">
          {SECTION_HEADINGS.crosswalk}
        </h2>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">{CROSSWALK.intro}</p>
        <div className="mt-4 grid grid-cols-2 items-start gap-4">
          <div className="rounded-lg border border-border bg-card px-4 py-2">
            <table className="w-full text-left">
              <thead>
                <tr>
                  {CROSSWALK.columns.map((column) => (
                    <th
                      key={column}
                      scope="col"
                      className="pb-2 pr-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crosswalk.map((row) => (
                  <tr key={row.deliveryKey} className="border-t border-hairline">
                    <td className="py-2 pr-3 text-sm text-foreground">{row.deliveryName}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{row.subLabel}</td>
                    <td className="py-2 pr-3">
                      <StatusPill tone="neutral">{row.engagementLabel}</StatusPill>
                    </td>
                    <td className="py-2 pr-1">
                      <span className="font-mono text-[0.75rem] font-semibold tracking-[0.06em] text-foreground">
                        {row.gateName}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <section className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <SectionLabel>{CROSSWALK.otherDirection}</SectionLabel>
            </div>
            <div className="px-4 py-3">
              <div className="flex flex-col gap-2">
                {reverse.map(({ phase, deliveryNames }) => (
                  <div
                    key={phase.key}
                    className="flex items-start justify-between gap-3 border-b border-hairline pb-2 last:border-0"
                  >
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {phase.number} · {phase.name}
                      </div>
                      <div className="text-xs text-muted-foreground">{phase.gateName}</div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-1">
                      {deliveryNames.length === 0 ? (
                        <StatusPill tone="review">{CROSSWALK.preProjectPill}</StatusPill>
                      ) : (
                        deliveryNames.map((name) => (
                          <StatusPill key={name} tone="neutral">
                            {name}
                          </StatusPill>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* 5 · the module inventory */}
        <h2 className="mt-8 font-serif text-xl font-semibold text-foreground">
          {SECTION_HEADINGS.inventory}
        </h2>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">{INVENTORY.intro}</p>
        <section aria-label={INVENTORY.totalsAriaLabel} className="mt-4 grid grid-cols-4 gap-4">
          {INVENTORY.kpis.map((kpi) => (
            <KpiCard key={kpi.label} label={kpi.label} figure={kpi.figure} qualifier={kpi.qualifier} />
          ))}
        </section>
        <p
          data-testid="reva-reconciliation"
          className="mt-3 flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold text-foreground">{INVENTORY.reconciliationLead}</span>{' '}
            {INVENTORY.reconciliationNote} {INVENTORY.reconciliationCounts}
          </span>
        </p>

        <div className="mt-4 grid grid-cols-2 items-start gap-4">
          {levels.map((card) => (
            <section key={card.level} className="rounded-lg border border-border bg-card px-4 py-3">
              <div className="flex items-baseline justify-between gap-2">
                <SectionLabel>{card.label}</SectionLabel>
                <span className="v-figure text-xs tabular-nums text-muted-foreground">
                  {card.modules.length}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {card.scope} · {card.sketch} {INVENTORY.fromSketch} · {card.addedCount}{' '}
                {INVENTORY.added}
              </p>
              <table className="mt-2 w-full text-left">
                <tbody>
                  {card.modules.map((module) => (
                    <tr key={module.id} className="border-t border-hairline" data-module={module.id}>
                      <td className="py-1.5 pr-3 text-sm text-foreground">{module.name}</td>
                      <td className="py-1.5 pr-3 text-xs text-muted-foreground">{module.what}</td>
                      <td className="py-1.5 pr-1 text-right">
                        <StatusPill tone={module.origin === 'sketch' ? 'neutral' : 'proposed'}>
                          {module.origin === 'sketch' ? INVENTORY.fromSketch : INVENTORY.added}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>

        <div className="mt-4">
          <section className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <SectionLabel>{INVENTORY.removedHeading}</SectionLabel>
              <span className="v-figure text-xs tabular-nums text-muted-foreground">
                {REMOVED_MODULES.length}
              </span>
            </div>
            <div className="px-4 py-3">
              {REMOVED_MODULES.map((removed) => (
                <div
                  key={removed.label}
                  data-testid="removed-module"
                  className="flex items-start justify-between gap-3 border-b border-hairline py-1.5 last:border-0"
                >
                  <div>
                    <div className="text-sm text-foreground">{removed.label}</div>
                    <div className="text-xs text-muted-foreground">{removed.why}</div>
                  </div>
                  <span className="shrink-0 rounded-md border border-border px-1.5 py-0.5 text-[0.625rem] text-muted-foreground">
                    {removed.level}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* 6 · run & evolve — named, not built */}
        <section className="mt-8 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <SectionLabel>{SECTION_HEADINGS.runAndEvolve}</SectionLabel>
            <StatusPill tone="neutral">{RUN_AND_EVOLVE_LABELS.pill}</StatusPill>
          </div>
          <div className="px-4 py-3">
            <div className="grid grid-cols-3 gap-4">
              {RUN_AND_EVOLVE.modules.map((module) => (
                <div key={module.id} className="rounded-md border border-dashed border-border px-3 py-2">
                  <div className="font-mono text-[0.75rem] font-semibold text-muted-foreground">
                    {module.id}
                  </div>
                  <div className="mt-0.5 text-sm text-ink-soft">{module.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {module.cadence} · {module.owner}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{RUN_AND_EVOLVE.why}</p>
          </div>
        </section>

        <DensityNote>{ARCHITECTURE_DENSITY_NOTE}</DensityNote>
      </div>
    </AppShell>
  )
}

/** Agent-leverage dots — filled = value, mint. Never colour alone: an sr-only reading follows. */
function Leverage({ value }: { value: number }) {
  const title = PHASES_TABLE.leverageTitle(value, MAX_LEVERAGE)
  return (
    <span className="inline-flex items-center gap-1" title={title}>
      {Array.from({ length: MAX_LEVERAGE }, (_, i) => (
        <span
          key={i}
          className={cn('h-1.5 w-1.5 rounded-full', i < value ? 'bg-mint' : 'bg-border')}
        />
      ))}
      <span className="sr-only">{title}</span>
    </span>
  )
}
